import * as Sentry from '@sentry/nextjs';
import axios from 'axios';
import type {
  Product,
  ProductDetail,
  ProductResponse,
  ProductReview,
  ProductReviewSchema
} from 'src/models/product/schema';
import type { LOCALE_TYPE } from 'src/types/locale';
import type { RankingApiParameter } from 'src/types/product';
import type { SortType } from 'src/types/productSort';
import { BackendApiFetch } from 'src/utils/backendApi';
import { convertLocaleType } from 'src/utils/convertLocaleType';
import { getAccessToken } from 'src/utils/cookies';
import { Left, Right } from 'src/utils/either';
import { errorMessage } from 'src/utils/errorMessage';
import { shoppingApiBaseUrl } from 'src/utils/shoppingApiBaseUrl';
import { getLocaleFromUrl } from 'src/utils/subdomain';
import { getRequest, postRequest } from '../wa/gateways/api/wamazingApi';
import type { ApiResponse } from './response/ApiResponse';

export enum Type {
  ALL = 'all',
  MAKER_ID = 'makerId',
  BRAND_ID = 'brandId',
  LARGE_CATEGORY_ID = 'largeCategoryId',
  MIDDLE_CATEGORY_ID = 'middleCategoryId',
  SMALL_CATEGORY_ID = 'smallCategoryId',
  TINY_CATEGORY_ID = 'tinyCategoryId',
  FEATURE_TAG_ID = 'featureTagId'
}

const fetchProducts = async (
  id: number,
  type: Type,
  locale: string,
  page: number,
  items: number,
  shopId?: number,
  additionalParam?:
    | {
        type: string;
        id: number;
      }
    | undefined,
  allOrder: boolean = true,
  isStaticProps = false,
  sort: SortType = ''
): Promise<ProductResponse> => {
  const basePath = shopId
    ? `/web/v1/products?shopId=${shopId}&page=${page}&items=${items}`
    : `/web/v1/products?page=${page}&items=${items}`;
  const path = type === 'all' ? basePath : `${basePath}&${type}=${id}`;
  const additionalParams = additionalParam ? `&${additionalParam.type}=${additionalParam.id}` : '';
  const sortParam = sort ? `&sort=${sort}` : '';
  try {
    const response = await BackendApiFetch<ProductResponse>({
      path: `${path}${additionalParams}${sortParam}&allOrder=${allOrder}`,
      localeType: locale,
      isStaticProps
    });
    return response.data;
  } catch (e) {
    throw new Error(errorMessage(e));
  }
};

// OpenAPI: https://develop.d1u4a820mt7mop.amplifyapp.com/#/Web%3A%3AV1%3A%3AProduct/get_kaimono_web_v1_products__id_
const fetchProductDetailById = async (
  id: number,
  locale: string,
  isStaticProps = false
): Promise<ApiResponse<ProductDetail>> => {
  try {
    const response = await BackendApiFetch<ProductDetail & { errors: string[] }>({
      path: `/web/v1/products/${id}`,
      localeType: locale,
      isStaticProps
    });
    if (response.statusCode !== 200) {
      return new Left({ errors: response.data.errors });
    }
    const product = response.data;
    return new Right(product);
  } catch (e) {
    throw new Error(errorMessage(e));
  }
};

export const fetchProductDetailByIdInClient = async (id: number): Promise<ApiResponse<ProductDetail>> => {
  const accessToken = getAccessToken();
  const authorizationHeader = {
    Authorization: `Bearer ${accessToken}`
  };
  try {
    const response = await getRequest<ProductDetail>({
      baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
      path: `/web/v1/products/${id}`,
      headers: authorizationHeader
    });
    return new Right(response);
  } catch (e) {
    throw new Error(errorMessage(e));
  }
};

const fetchProductReviewById = async (
  id: number,
  locale: LOCALE_TYPE,
  pageIndex: number
): Promise<ApiResponse<ProductReview>> => {
  try {
    const response = await BackendApiFetch<ProductReview & { errors: string[] }>({
      path: `/api/v1/products/${id}/product_reviews?page=${pageIndex}`,
      localeType: locale
    });
    if (response.statusCode !== 200) {
      return new Left({ errors: response.data.errors });
    }
    const review = response.data;
    return new Right(review);
  } catch (e) {
    throw new Error(errorMessage(e));
  }
};

const fetchProductReview = async (id: number, pageIndex: number): Promise<ApiResponse<ProductReview>> => {
  try {
    const response = await getRequest<ProductReview & { errors: string[] }>({
      baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
      path: `/api/v1/products/${id}/product_reviews?page=${pageIndex}`
    });
    if (response.errors && response.errors.length > 0) {
      return new Left({ errors: response.errors });
    }
    return new Right(response);
  } catch (e) {
    return new Left({ errors: [errorMessage(e)] });
  }
};

const postProductReview = async (id: number, params: ProductReviewSchema): Promise<ApiResponse<{}>> => {
  try {
    const response = await postRequest<{
      errors: string[];
    }>({
      baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
      path: `/api/v1/products/${id}/product_reviews`,
      params: {
        productReview: params
      }
    });

    if (response.errors && response.errors.length > 0) {
      return new Left({ errors: response.errors });
    }
    return new Right(true);
  } catch (e) {
    Sentry.captureException(errorMessage(e));
    return new Left({ errors: [errorMessage(e)] });
  }
};

const fetchAvailableProducts = async ({ ids }: { ids: number[] }) => {
  try {
    const products = await getRequest<{ productAvailabilities: { productId: number; orderable: boolean }[] }>({
      baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
      path: `/api/v1/products/availabilities?ids=[${ids}]`
    });
    return products;
  } catch (error) {
    throw new Error(errorMessage(error));
  }
};

const fetchProductsByIds = async (
  ids: number[],
  page: number,
  items: number,
  locale: string,
  isStaticProps = false
): Promise<Product[]> => {
  try {
    const response = await BackendApiFetch<ProductResponse>({
      path: `/web/v1/products?page=${page}&items=${items}&ids=[${ids}]&allOrder=true`,
      localeType: locale,
      isStaticProps
    });

    if (!response.data.products) return [];

    const products = response.data.products;
    return products;
  } catch (e) {
    throw new Error(errorMessage(e));
  }
};

const fetchOrderableProductIds = async () => {
  try {
    const response = await BackendApiFetch<{ productIds: string[] }>({
      path: '/app/v1/products/all_product_ids'
    });
    const ids = response.data.productIds;
    return ids;
  } catch (e) {
    Sentry.captureException(errorMessage(e));
    return [];
  }
};

const fetchProductsByRanking = async (
  params: Pick<RankingApiParameter, 'type'>,
  limit: number,
  locale: string
): Promise<ApiResponse<ProductDetail[]>> => {
  const response = await BackendApiFetch<ProductDetail[]>({
    path: `/api/v1/rankings?type=${params.type}&number=${limit}&allOrder=true`,
    localeType: locale
  });
  if (response.statusCode !== 200) {
    return new Left({ errors: [] });
  }
  return new Right(response.data);
};

const postProductArrivalNotices = async (productId: number, locale: string): Promise<ApiResponse<undefined>> => {
  try {
    const accessToken = getAccessToken();
    const authorizationHeader = {
      Authorization: `Bearer ${accessToken}`
    };
    await axios.post<{ errors: string[] }>(
      `${shoppingApiBaseUrl(getLocaleFromUrl())}/api/v1/product_arrival_notices`,
      {
        productId,
        locale: convertLocaleType(locale)
      },
      { headers: authorizationHeader }
    );

    return new Right(undefined);
  } catch (e) {
    return new Left({ errors: [errorMessage(e)] });
  }
};

export {
  fetchAvailableProducts,
  fetchOrderableProductIds,
  fetchProductDetailById,
  fetchProductReview,
  fetchProductReviewById,
  fetchProducts,
  fetchProductsByIds,
  fetchProductsByRanking,
  postProductArrivalNotices,
  postProductReview
};
